import { watch, onUnmounted, type Ref } from 'vue'
import { useVersionStore } from '@/stores/version'

/**
 * Composable to track forms with unsaved changes
 *
 * Registers/unregisters the form in the version store based on the isDirty ref.
 * This enables the router guard to warn users before navigating away from dirty forms.
 *
 * Usage:
 * ```ts
 * const isDirty = computed(() => hasFormChanged())
 * useDirtyFormGuard('account-form', isDirty)
 * ```
 *
 * @param formId - Unique identifier for the form
 * @param isDirty - Reactive ref indicating whether the form has unsaved changes
 */
export function useDirtyFormGuard(formId: string, isDirty: Ref<boolean>): void {
  const versionStore = useVersionStore()

  // Watch isDirty and register/unregister accordingly
  watch(
    isDirty,
    (dirty) => {
      if (dirty) {
        versionStore.registerDirtyForm(formId)
      } else {
        versionStore.unregisterDirtyForm(formId)
      }
    },
    { immediate: true }
  )

  // Cleanup on unmount
  onUnmounted(() => {
    versionStore.unregisterDirtyForm(formId)
  })
}
